import React, { useState, useEffect, useRef, useCallback } from "react";
import { Check, Crown, Loader2, ArrowLeft, Sparkles, Users, Award, DollarSign, TrendingUp, Gift, Share2, AlertCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FEATURES = [
  "Personalized meal plans & recipes",
  "AI-powered grocery list optimization",
  "Cross-store price comparison & cost analysis",
  "Community deals & sharing",
  "Smart pantry management",
  "Receipt scanning & budget tracking",
  "Deal alerts & notifications",
];

// ── PayPal Button Component ─────────────────────────────────────────────

const PayPalSubscribeButton = ({ planId, planType, clientId, token, promoCode, onSuccess, onError }) => {
  const containerRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [rendering, setRendering] = useState(false);
  const renderedRef = useRef(false);

  // Load PayPal JS SDK
  useEffect(() => {
    if (!clientId || !planId) return;
    const scriptId = "paypal-sdk-script";

    // Check if already loaded
    if (window.paypal) {
      setSdkReady(true);
      return;
    }

    // Check if script tag exists but hasn't loaded yet
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.addEventListener("load", () => setSdkReady(true));
      return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&vault=true&intent=subscription`;
    script.addEventListener("load", () => setSdkReady(true));
    script.addEventListener("error", () => onError?.("Failed to load PayPal SDK"));
    document.body.appendChild(script);

    return () => {
      // Don't remove — shared across buttons
    };
  }, [clientId, planId, onError]);

  // Render the PayPal button
  useEffect(() => {
    if (!sdkReady || !window.paypal || !containerRef.current || renderedRef.current || rendering) return;
    setRendering(true);
    renderedRef.current = true;

    // Clear container
    containerRef.current.innerHTML = "";

    try {
      window.paypal.Buttons({
        style: {
          shape: "pill",
          color: "gold",
          layout: "vertical",
          label: "subscribe",
        },
        createSubscription: (data, actions) => {
          return actions.subscription.create({
            plan_id: planId,
            application_context: {
              shipping_preference: "NO_SHIPPING",
            },
          });
        },
        onApprove: async (data) => {
          // Subscription approved! Activate in our backend
          try {
            const res = await fetch(`${BACKEND_URL}/api/subscription/paypal-activate`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                subscription_id: data.subscriptionID,
                plan: planType,
                promo_code: promoCode || "",
              }),
            });
            if (res.ok) {
              const result = await res.json();
              onSuccess?.(result);
            } else {
              const err = await res.json().catch(() => ({}));
              onError?.(err.detail || "Failed to activate subscription");
            }
          } catch (e) {
            onError?.("Network error activating subscription");
          }
        },
        onError: (err) => {
          console.error("PayPal button error:", err);
          onError?.("PayPal encountered an error. Please try again.");
        },
        onCancel: () => {
          // User closed the popup
        },
      }).render(containerRef.current).catch((err) => {
        console.error("PayPal render error:", err);
      });
    } catch (e) {
      console.error("PayPal Buttons error:", e);
    }
    setRendering(false);
  }, [sdkReady, planId, planType, token, promoCode, onSuccess, onError, rendering]);

  return (
    <div>
      <div ref={containerRef} data-testid={`paypal-btn-${planType}`} />
      {!sdkReady && (
        <div className="flex items-center justify-center py-3 text-gray-400 text-xs gap-2">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading PayPal...
        </div>
      )}
    </div>
  );
};


// ── Main Subscription Page ──────────────────────────────────────────────

const Subscription = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [subStatus, setSubStatus] = useState(null);
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [activationResult, setActivationResult] = useState(null);
  const [error, setError] = useState("");

  const isSuccessReturn = searchParams.get("subscription_id") || searchParams.get("session_id");

  // Load subscription status + PayPal config
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const [statusRes, configRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/subscription/status`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${BACKEND_URL}/api/subscription/paypal-config`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        if (statusRes.ok) setSubStatus(await statusRes.json());
        if (configRes.ok) {
          const cfg = await configRes.json();
          setPaypalConfig(cfg);
          if (cfg.promo_code) {
            setPromoCode(cfg.promo_code);
            setPromoApplied({ percent: cfg.promo_discount, code: cfg.promo_code });
          }
        }
      } catch { /* ignore */ }
      setLoadingConfig(false);
    };
    load();
  }, [token]);

  // Handle successful return from redirect flow (legacy fallback)
  useEffect(() => {
    if (isSuccessReturn && token) {
      const checkoutId = searchParams.get("subscription_id") || localStorage.getItem("paypal_subscription_id");
      if (checkoutId) {
        fetch(`${BACKEND_URL}/api/subscription/checkout/status/${checkoutId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(res => res.ok ? res.json() : null).then(data => {
          if (data) setActivationResult({ success: true, plan: data.plan || "monthly", subscription_id: checkoutId });
          localStorage.removeItem("paypal_subscription_id");
        }).catch(() => {});
      }
    }
  }, [isSuccessReturn, token, searchParams]);

  const handlePromoApply = async () => {
    if (!promoCode.trim()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan: "monthly", origin_url: window.location.origin, promo_code: promoCode, dry_run: true }),
      });
      // Even if it creates a subscription, the promo validation happens server-side on activate
      setPromoApplied({ percent: 20, code: promoCode });
    } catch { /* ignore */ }
  };

  const onSubscriptionSuccess = useCallback((result) => {
    setActivationResult(result);
    setSubStatus({ plan: result.plan, status: "active" });
  }, []);

  const onSubscriptionError = useCallback((msg) => {
    setError(typeof msg === "string" ? msg : "Subscription failed. Please try again.");
    setTimeout(() => setError(""), 5000);
  }, []);

  // Redirect-based checkout fallback (in case JS SDK doesn't render)
  const handleRedirectCheckout = async (plan) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/subscription/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan, origin_url: window.location.origin, promo_code: promoCode }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.subscription_id) localStorage.setItem("paypal_subscription_id", data.subscription_id);
        if (data.url) window.location.href = data.url;
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Checkout failed");
      }
    } catch { setError("Network error"); }
  };

  // Success state
  if (activationResult?.success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="payment-success">
        <Card className="w-full max-w-sm">
          <CardContent className="p-6 text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Welcome to Premium!</h2>
            <p className="text-sm text-gray-600">
              Your {activationResult.plan} subscription is now active. Enjoy all premium features!
            </p>
            {activationResult.discount_applied && (
              <p className="text-xs text-green-600 font-medium">{activationResult.discount_applied}</p>
            )}
            <Button className="w-full bg-green-500 text-white" onClick={() => navigate("/")}>
              Start Exploring
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isPremium = subStatus && (subStatus.plan === "monthly" || subStatus.plan === "annual");
  const isTrial = subStatus?.plan === "trial";

  return (
    <div className="p-4 space-y-5 pb-24" data-testid="subscription-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="h-5 w-5 text-gray-600" />
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Subscription</h2>
          <p className="text-sm text-gray-500">Choose your plan</p>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="error-banner">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Current Status */}
      {subStatus && (
        <Card className={isPremium ? "bg-green-50 border-green-200" : isTrial ? "bg-blue-50 border-blue-200" : "bg-gray-50"}>
          <CardContent className="p-4 flex items-center gap-3">
            <Crown className={`h-6 w-6 ${isPremium ? "text-green-600" : isTrial ? "text-blue-600" : "text-gray-400"}`} />
            <div>
              <p className="font-semibold text-gray-900 capitalize">{subStatus.plan} Plan</p>
              {isTrial && <p className="text-xs text-blue-600">{subStatus.days_left} days left in trial</p>}
              {isPremium && <p className="text-xs text-green-600">Active subscription</p>}
              {subStatus.plan === "free" && <p className="text-xs text-gray-500">Limited features</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plans with PayPal Buttons */}
      {!isPremium && (
        <div className="space-y-4">
          {/* Monthly Plan */}
          <Card className="border-2 border-gray-200 hover:border-green-400 transition-colors">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">Monthly</h3>
                  <p className="text-sm text-gray-500">Billed monthly</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">$4.99</p>
                  <p className="text-xs text-gray-400">/month</p>
                  {promoApplied && (
                    <p className="text-xs text-green-600 font-medium">
                      ${(4.99 * (1 - promoApplied.percent / 100)).toFixed(2)} with {promoApplied.percent}% off
                    </p>
                  )}
                </div>
              </div>
              {paypalConfig?.client_id && paypalConfig?.monthly_plan_id ? (
                <PayPalSubscribeButton
                  planId={paypalConfig.monthly_plan_id}
                  planType="monthly"
                  clientId={paypalConfig.client_id}
                  token={token}
                  promoCode={promoCode}
                  onSuccess={onSubscriptionSuccess}
                  onError={onSubscriptionError}
                />
              ) : loadingConfig ? (
                <div className="flex items-center justify-center py-3 text-gray-400 text-xs gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                </div>
              ) : (
                <Button
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                  onClick={() => handleRedirectCheckout("monthly")}
                  data-testid="checkout-monthly-fallback"
                >
                  Subscribe Monthly
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Annual Plan */}
          <Card className="border-2 border-green-500 relative">
            <Badge className="absolute -top-2.5 left-4 bg-green-500 text-white text-xs">Save 25%</Badge>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">Annual</h3>
                  <p className="text-sm text-gray-500">Billed yearly</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">$45.00</p>
                  <p className="text-xs text-gray-400">/year ($3.75/mo)</p>
                  {promoApplied && (
                    <p className="text-xs text-green-600 font-medium">
                      ${(45.00 * (1 - promoApplied.percent / 100)).toFixed(2)} with {promoApplied.percent}% off
                    </p>
                  )}
                </div>
              </div>
              {paypalConfig?.client_id && paypalConfig?.annual_plan_id ? (
                <PayPalSubscribeButton
                  planId={paypalConfig.annual_plan_id}
                  planType="annual"
                  clientId={paypalConfig.client_id}
                  token={token}
                  promoCode={promoCode}
                  onSuccess={onSubscriptionSuccess}
                  onError={onSubscriptionError}
                />
              ) : loadingConfig ? (
                <div className="flex items-center justify-center py-3 text-gray-400 text-xs gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading...
                </div>
              ) : (
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => handleRedirectCheckout("annual")}
                  data-testid="checkout-annual-fallback"
                >
                  <Crown className="h-4 w-4 mr-2" /> Subscribe Annually
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Promo Code */}
          <Card className="border border-dashed border-gray-300">
            <CardContent className="p-3">
              <div className="flex gap-2 items-center">
                <Gift className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Promo code (e.g., SAVE20-XXXXXX)"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                  className="flex-1 text-sm border border-gray-200 rounded-md px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-green-400"
                  data-testid="promo-code-input"
                />
              </div>
              {promoCode && !promoApplied && (
                <p className="text-xs text-amber-600 mt-1 ml-6">Code will be applied when you subscribe</p>
              )}
              {promoApplied && (
                <p className="text-xs text-green-600 mt-1 ml-6 font-medium">
                  {promoApplied.percent}% discount applied!
                </p>
              )}
            </CardContent>
          </Card>

          {/* Payment Provider Badge */}
          <div className="flex items-center justify-center gap-2 py-1">
            <span className="text-xs text-gray-400">Secured by</span>
            <span className="text-xs font-bold text-blue-700">PayPal</span>
          </div>
        </div>
      )}

      {/* Features */}
      <Card>
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Premium Features</p>
          <div className="space-y-2">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span className="text-sm text-gray-700">{f}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Gamification / Referral Info */}
      <Card className="bg-gradient-to-r from-violet-50 to-fuchsia-50 border-violet-200">
        <CardContent className="p-4 space-y-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Award className="h-5 w-5 text-violet-500" />
            How Gamification Works
          </h3>
          <div className="space-y-2 text-xs text-gray-600">
            <div className="flex items-start gap-2">
              <Sparkles className="h-3 w-3 text-amber-500 mt-0.5 flex-shrink-0" />
              <p>Earn points by posting deals, verifying deals, and engaging with the community.</p>
            </div>
            <div className="flex items-start gap-2">
              <TrendingUp className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0" />
              <p>Climb the leaderboard and unlock achievement badges.</p>
            </div>
            <div className="flex items-start gap-2">
              <DollarSign className="h-3 w-3 text-green-600 mt-0.5 flex-shrink-0" />
              <p>Top contributors can earn subscription discounts based on community activity.</p>
            </div>
            <div className="flex items-start gap-2">
              <Share2 className="h-3 w-3 text-blue-500 mt-0.5 flex-shrink-0" />
              <p>
                <strong>Refer friends</strong> and earn $1.00 every month a referred friend stays subscribed.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Subscription;
